<script setup lang="ts">
import { InputAutoCompletion } from '@sancare/typescript-commons'
import type { CompletionOption } from '@sancare/typescript-commons/src/components/types'
import { UseMapToCompletionOptions } from '@sancare/typescript-commons/src/composables/useMapToCompletionOptions'
import {
  useAutoCompletionDebouncedWatcher
} from '@sancare/typescript-commons/src/misc/composables/useAutoCompletionDebouncedWatcher'
import type { FinessWithoutLongDescription } from '@sancare/typescript-commons/src/types/finess'
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

export interface AppFinessInputProps {
  settingName: string
}

const props = defineProps<AppFinessInputProps>()

const emit = defineEmits<{
  (e: 'select-finess', value: { finess: string, shortDescription: string, settingName: string }): void
}>()

const store = useStore()

const showCompletion = ref(false)
const finessInput = ref('')

const searchFinessRequestName = computed(() => props.settingName === 'restricted_legal_finess'
  ? 'searchLegalFiness'
  : 'searchGeoFiness'
)
const clearFinessListRequestName = computed(() => props.settingName === 'restricted_legal_finess'
  ? 'searchLegalFinessClear'
  : 'searchGeoFinessClear'
)

const completionFinessList = computed<CompletionOption<FinessWithoutLongDescription>[]>(() => UseMapToCompletionOptions<FinessWithoutLongDescription>(
  props.settingName === 'restricted_legal_finess'
    ? store.state.admin.legalFinessList
    : store.state.admin.geoFinessList,
  'finess',
  'shortDescription',
  'finess'
))

const searchFinessRequest = computed(() => props.settingName === 'restricted_legal_finess'
  ? store.state.admin.searchLegalFiness
  : store.state.admin.searchGeoFiness
)

onMounted(() => {
  useAutoCompletionDebouncedWatcher(
    finessInput,
    completionFinessList,
    (search, signal) => fetchFinessData(search, signal),
    () => clearFinessData()
  )
})

function selectFiness(finess: FinessWithoutLongDescription) {
  closeCompletion()
  emit('select-finess', {
    finess: finess.finess,
    shortDescription: finess.shortDescription,
    settingName: props.settingName
  })
  finessInput.value = ''
}

function fetchFinessData(search: string, signal: AbortSignal) {
  store.dispatch(searchFinessRequestName.value, { search, signal })
}

function clearFinessData() {
  store.commit(clearFinessListRequestName.value)
}

function openCompletion() {
  showCompletion.value = true
}

function closeCompletion() {
  showCompletion.value = false
}
</script>

<template>
  <div>
    <input
      v-model="finessInput"
      v-on-click-outside="closeCompletion"
      class="form-control"
      type="text"
      placeholder="Nom ou FINESS de l'établissement"
      @click="openCompletion"
      @keydown.esc="closeCompletion"
    >
    <!-- @vue-generic {FinessWithoutLongDescription} -->
    <input-auto-completion
      v-show="showCompletion && finessInput !== ''"
      :completion-options="completionFinessList"
      :is-loading="searchFinessRequest.fetching"
      @select-option="selectFiness"
    />
  </div>
</template>
